import {
    FEATURE_COUPONS,
    FEATURE_GIFT_CERTIFICATES, FEATURE_INVOICES, FEATURE_MARKETING,
    FEATURE_PACKAGES,
    FEATURE_SUBSCRIPTIONS,
    FEATURE_WAITLIST,
    FEATURE_CUSTOM_QUESTIONS,
    useFeatures
} from '~/lib/Features';
import GoogleAnalyticsIcon from '~/components/icons/GoogleAnalyticsIcon';
import TextInput from '~/components/TextInput';
import IntegrationSettingsBlock from '~/components/integrations/IntegrationSettingsBlock';
import IntegrationBlock from '~/components/integrations/IntegrationBlock';
import { ArchiveBoxIcon, ArrowPathIcon, MegaphoneIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import CertificateIcon from '~/components/icons/CertificateIcon';
import { useState } from 'react';
import * as Toast from '~/lib/Toast';
import { Link } from 'react-router-dom';
import { getBusinessUrl } from '~/lib/useBusinessNavigate';
import Toggle from '~/components/Toggle';
import ClipboardTextIcon from '~/components/icons/ClipboardTextIcon';
import GiftIcon from '~/components/icons/GiftIcon';
import InvoiceIcon from '~/components/icons/InvoiceIcon.jsx';

const featureDescriptions = [
    {
        key: FEATURE_INVOICES,
        title: 'Invoices',
        href: '/invoices',
        icon: <InvoiceIcon />,
        description: 'Send invoices to clients',
    },
    {
        key: FEATURE_PACKAGES,
        title: 'Packages',
        icon: <ArchiveBoxIcon />,
        href: '/packages',
        description:
            'Allow clients to pre-pay for multiple appointments at once',
    },
    {
        key: FEATURE_SUBSCRIPTIONS,
        title: 'Subscriptions',
        href: '/subscriptions',
        icon: <ArrowPathIcon />,
        description:
            'Subscriptions allow your clients to book into classes or appointments using pre-paid credits.',
    },
    {
        key: FEATURE_COUPONS,
        title: 'Coupons',
        href: '/coupons',
        icon: <CertificateIcon size={32} />,
        description: 'Offer discount codes & promotions to clients',
    },
    {
        key: FEATURE_WAITLIST,
        title: 'Waitlisting',
        href: '/waitlist',
        icon: <ClipboardTextIcon size={32} />,
        description:
            'Clients can add themselves to a waitlist if a time is unavailable',
    },
    {
        key: FEATURE_GIFT_CERTIFICATES,
        title: 'Gift Certificates',
        href: '/gift-certificates',
        icon: <GiftIcon />,
        description: 'Sell gift certificates',
    },
    {
        key: FEATURE_CUSTOM_QUESTIONS,
        title: 'Extensions',
        href: '/custom-questions',
        icon: <PuzzlePieceIcon />,
        description:
            'Write your own question types in HTML, CSS and JavaScript and add them to your forms',
        experimental: true,
    },
    {
        key: FEATURE_MARKETING,
        title: 'Marketing Automations',
        href: '/marketing',
        icon: <MegaphoneIcon />,
        description: 'Create automations for marketing and operations',
        experimental: true,
    },
];

const FeatureToggle = ({ enabled, featureKey, onChange, isSaving }) => {
    const [isToggling, setIsToggling] = useState(false);
    function toggle(newEnabled) {
        setIsToggling(true);
        const featureName = featureDescriptions.find(
            f => f.key === featureKey,
        )?.title;
        onChange(featureKey, newEnabled)
            .then(() => {
                if (newEnabled) {
                    Toast.success(
                        `Enabled ${featureName}, you can now set this up by navigating to the ${featureName} section`,
                    );
                } else {
                    Toast.success(`Disabled ${featureName}`);
                }
            })
            .finally(() => setIsToggling(false));
    }

    isSaving = isSaving && isToggling;

    return (
        <div className={`flex items-center ${isToggling ? 'opacity-75' : ''}`}>
            <Toggle enabled={enabled} setEnabled={toggle} className="mr-2" />
            <div
                className={`${enabled ? 'text-primary-700' : 'text-gray-500'}`}
            >
                {enabled && (isToggling ? 'Disabling...' : 'Enabled')}
                {!enabled && (isToggling ? 'Enabling...' : 'Disabled')}
            </div>
        </div>
    );
};

const BreelyFeatures = ({}) => {
    const { isEnabled, toggle, isSaving } = useFeatures();

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 max-w-4xl gap-6">
            {featureDescriptions.map(feature => (
                <IntegrationBlock
                    key={feature.key}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    experimental={feature.experimental}
                    //enabled={isEnabled(feature.key)}
                    footer={
                        <div className="flex items-center justify-between">
                            <FeatureToggle
                                enabled={isEnabled(feature.key)}
                                key={feature.key}
                                featureKey={feature.key}
                                onChange={toggle}
                                isSaving={isSaving}
                            />
                            {isEnabled(feature.key) && (
                                <Link
                                    to={getBusinessUrl(feature.href)}
                                    className="no-underline"
                                >
                                    Go to {feature.title} →
                                </Link>
                            )}
                        </div>
                    }
                />
            ))}
        </div>
    );
};

export default BreelyFeatures;
